import { createEntityAdapter, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { RootState } from '../rootReducer'

export interface IPresentation {
  id: string
  name: string
  slides: string[] // URLs of slide images
  createdAt: number
}

export interface IVideo {
  id: string
  name: string
  url: string
  thumbnailUrl?: string
  duration?: number
  createdAt: number
}

export interface IAudio {
  id: string
  name: string
  url: string
  duration?: number
  createdAt: number
}

export interface IMediaState {
  isOpen: boolean
  presentations: IPresentation[]
  videos: IVideo[]
  audios: IAudio[]
  isLoading: boolean
  activeMediaId: string | null
  activeMediaType: 'presentation' | 'video' | 'audio' | null
  currentSlideIndex: number
  // For audio playback state
  playingAudioId: string | null
  audioCurrentTime: number
}

export const initialMediaState: IMediaState = {
  isOpen: false,
  presentations: [],
  videos: [],
  audios: [],
  isLoading: false,
  activeMediaId: null,
  activeMediaType: null,
  currentSlideIndex: 0,
  playingAudioId: null,
  audioCurrentTime: 0,
}

const mediaAdapter = createEntityAdapter()

export const mediaSlice = createSlice({
  name: 'media',
  initialState: mediaAdapter.getInitialState(initialMediaState),
  reducers: {
    setIsOpenMedia: (state, action: PayloadAction<boolean>) => {
      state.isOpen = action.payload
    },
    setMediaLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload
    },
    // Presentations
    addPresentation: (state, action: PayloadAction<IPresentation>) => {
      state.presentations.push(action.payload)
    },
    removePresentation: (state, action: PayloadAction<string>) => {
      state.presentations = state.presentations.filter((p) => p.id !== action.payload)
      if (state.activeMediaId === action.payload) {
        state.activeMediaId = null
        state.activeMediaType = null
      }
    },
    setPresentations: (state, action: PayloadAction<IPresentation[]>) => {
      state.presentations = action.payload
    },
    // Videos
    addVideo: (state, action: PayloadAction<IVideo>) => {
      state.videos.push(action.payload)
    },
    removeVideo: (state, action: PayloadAction<string>) => {
      state.videos = state.videos.filter((v) => v.id !== action.payload)
      if (state.activeMediaId === action.payload) {
        state.activeMediaId = null
        state.activeMediaType = null
      }
    },
    setVideos: (state, action: PayloadAction<IVideo[]>) => {
      state.videos = action.payload
    },
    // Audios
    addAudio: (state, action: PayloadAction<IAudio>) => {
      state.audios.push(action.payload)
    },
    removeAudio: (state, action: PayloadAction<string>) => {
      state.audios = state.audios.filter((a) => a.id !== action.payload)
      if (state.activeMediaId === action.payload) {
        state.activeMediaId = null
        state.activeMediaType = null
      }
      if (state.playingAudioId === action.payload) {
        state.playingAudioId = null
      }
    },
    setAudios: (state, action: PayloadAction<IAudio[]>) => {
      state.audios = action.payload
    },
    // Active media (for broadcasting)
    setActiveMedia: (
      state,
      action: PayloadAction<{
        id: string | null
        type: 'presentation' | 'video' | 'audio' | null
      }>
    ) => {
      state.activeMediaId = action.payload.id
      state.activeMediaType = action.payload.type
      state.currentSlideIndex = 0
    },
    setCurrentSlideIndex: (state, action: PayloadAction<number>) => {
      state.currentSlideIndex = action.payload
    },
    // Audio playback
    setPlayingAudioId: (state, action: PayloadAction<string | null>) => {
      state.playingAudioId = action.payload
    },
    setAudioCurrentTime: (state, action: PayloadAction<number>) => {
      state.audioCurrentTime = action.payload
    },
    // Clear all media
    clearAllMedia: (state) => {
      state.presentations = []
      state.videos = []
      state.audios = []
      state.activeMediaId = null
      state.activeMediaType = null
      state.playingAudioId = null
    },
  },
})

export const {
  setIsOpenMedia,
  setMediaLoading,
  addPresentation,
  removePresentation,
  setPresentations,
  addVideo,
  removeVideo,
  setVideos,
  addAudio,
  removeAudio,
  setAudios,
  setActiveMedia,
  setCurrentSlideIndex,
  setPlayingAudioId,
  setAudioCurrentTime,
  clearAllMedia,
} = mediaSlice.actions

export const { selectAll: mediaSelector } = mediaAdapter.getSelectors(
  (state: RootState) => state[mediaSlice.name]
)

export const selectMediaState = (state: RootState) => state.media
export const selectIsOpenMedia = (state: RootState) => state.media.isOpen
export const selectMediaLoading = (state: RootState) => state.media.isLoading
export const selectPresentations = (state: RootState) => state.media.presentations
export const selectVideos = (state: RootState) => state.media.videos
export const selectAudios = (state: RootState) => state.media.audios
export const selectActiveMedia = (state: RootState) => ({
  id: state.media.activeMediaId,
  type: state.media.activeMediaType,
})
export const selectCurrentSlideIndex = (state: RootState) => state.media.currentSlideIndex
export const selectPlayingAudioId = (state: RootState) => state.media.playingAudioId
export const selectAudioCurrentTime = (state: RootState) => state.media.audioCurrentTime
